<template>
  <div class="public-info-container py-5">
    <div class="container">
      
      <div class="text-center mb-5">
        <h2 class="section-title text-primary text-uppercase fw-bold">
          <i class="fa fa-folder-open me-2"></i> {{ $t("public_info.title") }}
        </h2>
      </div>

      <div class="filter-card card border-0 shadow-sm mb-5">
        <div class="card-body p-4">
          <div class="row g-3 align-items-center justify-content-center">
            
            <div class="col-md-5">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fa fa-search text-muted"></i>
                </span>
                <input 
                  v-model="searchQuery" 
                  @input="handleFilterChange" 
                  class="form-control border-start-0 ps-0" 
                  :placeholder="$t('public_info.search')"
                >
              </div>
            </div>

            <div class="col-md-5 position-relative">
              <div class="custom-select-wrapper">
                <div class="input-group" @click="toggleDropdown">
                  <span class="input-group-text bg-white border-end-0">
                    <i class="fa fa-list-alt text-muted"></i>
                  </span>
                  <input 
                    type="text" 
                    class="form-control border-start-0 ps-0"
                    v-model="categorySearchTerm"
                    :placeholder="$t('public_info.all_categories')"
                    @input="onCategoryType"
                    @focus="showDropdown = true"
                  >
                  <span class="input-group-text bg-white" v-if="selectedCategorie">
                    <i class="fa fa-times text-danger cursor-pointer" @click.stop="clearCategory"></i>
                  </span>
                  <span class="input-group-text bg-white">
                    <i class="fa fa-chevron-down small text-muted"></i>
                  </span>
                </div>

                <ul v-if="showDropdown" class="custom-dropdown-list shadow">
                  <li 
                    v-for="cat in filteredCategories" 
                    :key="cat.id" 
                    @click="selectCategory(cat)"
                    class="dropdown-item"
                  >
                    {{ getCategoryName(cat) }}
                  </li>
                  <li v-if="filteredCategories.length === 0" class="dropdown-item text-muted text-center">
                    Nu există rezultate
                  </li>
                </ul>
              </div>
              <div v-if="showDropdown" class="click-overlay" @click="showDropdown = false"></div>
            </div>

          </div>
        </div>
      </div>

      <div v-if="documente.length > 0" class="document-grid">
        <div class="document-item" v-for="document in documente" :key="document.id">
          <div class="card doc-card h-100 border-0 shadow-sm">
            <div class="card-body d-flex flex-column text-center p-4">
              
              <div class="icon-wrapper mb-3">
                <i class="fa fa-file-pdf-o fa-3x text-danger opacity-75"></i>
              </div>

              <h5 class="card-title fw-bold text-dark mb-3">{{ getDocumentTitle(document) }}</h5>
              
              <div class="mt-auto">
                <span class="badge bg-light text-dark border mb-3">
                  {{ getCategoryName(document.categorie) }}
                </span>
                
                <a :href="document.document" target="_blank" class="btn btn-primary w-100 rounded-pill mt-2 download-btn">
                  <i class="fa fa-download me-2"></i> {{ $t("public_info.download") }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="alert alert-light border text-center py-5 mt-4">
        <i class="fa fa-search fa-2x text-muted mb-3"></i>
        <h5 class="text-muted">{{ $t("public_info.no_documents") }}</h5>
      </div>

      <div v-if="totalPages > 1" class="pagination-container mt-5">
        <nav aria-label="Page navigation">
          <ul class="pagination pagination-custom">
            <li class="page-item" :class="{ disabled: page === 1 }">
              <button class="page-link" @click="changePage(page - 1)">
                <i class="fa fa-chevron-left"></i>
              </button>
            </li>
            
            <li v-for="p in totalPages" :key="p" class="page-item" :class="{ active: p === page }">
              <button class="page-link fw-bold" @click="changePage(p)">{{ p }}</button>
            </li>

            <li class="page-item" :class="{ disabled: page === totalPages }">
              <button class="page-link" @click="changePage(page + 1)">
                <i class="fa fa-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useI18n } from "vue-i18n";

export default {
  data() {
    return {
      documente: [],
      categorii: [],
      searchQuery: "",
      selectedCategorie: "",     // ID-ul categoriei selectate (pentru API)
      categorySearchTerm: "",    // Textul pe care îl scrie userul în input
      showDropdown: false,       // Controlează afișarea listei
      page: 1,
      totalPages: 1,
      perPage: 12,
    };
  },
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  computed: {
    currentLanguage() {
      return this.locale;
    },
    // Filtrează categoriile local, pe baza a ce scrie userul
    filteredCategories() {
      if (!this.categorySearchTerm || this.selectedCategorie) {
        return this.categorii;
      }
      const term = this.categorySearchTerm.toLowerCase();
      return this.categorii.filter(cat => 
        this.getCategoryName(cat).toLowerCase().includes(term)
      );
    }
  },
  methods: {
    async fetchDocumente() {
      try {
        const response = await axios.get(`/informatii-publice/`, {
          params: {
            search: this.searchQuery,
            categorie: this.selectedCategorie,
            page: this.page,
            page_size: this.perPage,
          },
        });
        this.documente = response.data.results || [];
        const totalCount = response.data.count || 0;
        this.totalPages = Math.ceil(totalCount / this.perPage);
      } catch (error) {
        console.error("Eroare la preluarea documentelor:", error);
      }
    },
    async fetchCategorii() {
      try {
        const response = await axios.get(`/public-info-categories/`);
        this.categorii = response.data;
      } catch (error) {
        console.error("Eroare la preluarea categoriilor:", error);
      }
    },
    // --- Logica pentru Dropdown-ul Custom ---
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    onCategoryType() {
      // Când scrie, resetăm ID-ul selectat ca să permitem căutarea nouă
      this.selectedCategorie = "";
      this.showDropdown = true;
    },
    selectCategory(cat) {
      this.selectedCategorie = cat.id;
      this.categorySearchTerm = this.getCategoryName(cat); // Punem numele în input
      this.showDropdown = false;
      this.handleFilterChange(); // Declanșăm filtrarea documentelor
    },
    clearCategory() {
      this.selectedCategorie = "";
      this.categorySearchTerm = "";
      this.handleFilterChange();
    },
    // ----------------------------------------

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.page = newPage;
        this.fetchDocumente();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    handleFilterChange() {
      this.page = 1;
      this.fetchDocumente();
    },
    getCategoryName(categorie) {
      if (!categorie) return "";
      return this.currentLanguage === "en" ? categorie.nume_en : this.currentLanguage === "bg" ? categorie.nume_bg : categorie.nume_ro;
    },
    getDocumentTitle(document) {
      if (!document) return "";
      return this.currentLanguage === "en" ? document.nume_en : this.currentLanguage === "bg" ? document.nume_bg : document.nume_ro;
    }
  },
  mounted() {
    this.fetchCategorii();
    this.fetchDocumente();
  },
};
</script>

<style scoped>
/* Fundal general */
.public-info-container {
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Stiluri pentru Inputuri */
.form-control:focus {
  box-shadow: none;
  border-color: #dee2e6;
  background-color: #fff;
}
.input-group-text {
  border-color: #dee2e6;
}

/* Custom Dropdown Styles */
.custom-select-wrapper {
  position: relative;
}
.custom-dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  margin-top: 5px;
  padding: 0;
  list-style: none;
  max-height: 300px; /* Scroll dacă lista e lungă */
  overflow-y: auto;
  z-index: 1000;
  border: 1px solid #eee;
}
.custom-dropdown-list .dropdown-item {
  padding: 10px 15px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f8f9fa;
}
.custom-dropdown-list .dropdown-item:hover {
  background-color: #e9ecef;
  color: #004a99;
}
.click-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  z-index: 999;
  background: transparent;
}
.cursor-pointer {
  cursor: pointer;
}

/* Grid și Carduri */
.document-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
}

.doc-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 12px;
  background: white;
}
.doc-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

.icon-wrapper {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-btn {
  background-color: #004a99;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background 0.3s;
}
.download-btn:hover {
  background-color: #003366;
}

/* Paginare */
.pagination-container { display: flex; justify-content: center; }
.pagination-custom .page-item { margin: 0 3px; }
.pagination-custom .page-link {
  border: none;
  border-radius: 8px !important;
  color: #004a99;
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}
.pagination-custom .page-item.active .page-link {
  background-color: #004a99;
  color: white;
  box-shadow: 0 4px 10px rgba(0, 74, 153, 0.3);
}
.pagination-custom .page-item.disabled .page-link {
  background-color: transparent;
  color: #ccc;
  box-shadow: none;
}
</style>